import { createSlice } from "@reduxjs/toolkit";
import {
  createPulmonologyRecord,
  deletePulmonologyRecord,
  fetchPulmonologyRecords,
  updatePulmonologyRecord,
} from "../thunks/pulmonologyThunks";

const initialState = { byPatient: {}, loading: false, saving: false, error: null };
const normalizeRecord = (record) => record?.data ?? record;

const upsertRecord = (records, record) => {
  const normalized = normalizeRecord(record);
  const id = normalized?.id ?? normalized?._id;
  if (id == null) return records;
  const index = records.findIndex((item) => String(item?.id ?? item?._id) === String(id));
  if (index < 0) return [...records, normalized];
  return records.map((item, itemIndex) => itemIndex === index ? { ...item, ...normalized } : item);
};

const pulmonologySlice = createSlice({
  name: "pulmonology",
  initialState,
  reducers: {
    clearPulmonologyError(state) { state.error = null; },
    clearPulmonologyPatient(state, action) { delete state.byPatient[String(action.payload)]; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPulmonologyRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPulmonologyRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.byPatient[action.payload.patientId] = action.payload.records;
      })
      .addCase(fetchPulmonologyRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(createPulmonologyRecord.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createPulmonologyRecord.fulfilled, (state, action) => {
        state.saving = false;
        const { patientId, resource, record } = action.payload;
        const patientRecords = state.byPatient[patientId] || (state.byPatient[patientId] = {});
        patientRecords[resource] = upsertRecord(patientRecords[resource] || [], record);
      })
      .addCase(createPulmonologyRecord.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(updatePulmonologyRecord.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updatePulmonologyRecord.fulfilled, (state, action) => {
        state.saving = false;
        const { patientId, resource, record } = action.payload;
        const patientRecords = state.byPatient[patientId] || (state.byPatient[patientId] = {});
        patientRecords[resource] = upsertRecord(patientRecords[resource] || [], record);
      })
      .addCase(updatePulmonologyRecord.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(deletePulmonologyRecord.fulfilled, (state, action) => {
        const { patientId, resource, id } = action.payload;
        const patientRecords = state.byPatient[patientId];
        if (!patientRecords?.[resource]) return;
        patientRecords[resource] = patientRecords[resource].filter(
          (record) => String(record?.id ?? record?._id) !== id
        );
      })
      .addCase(deletePulmonologyRecord.rejected, (state, action) => {
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const { clearPulmonologyError, clearPulmonologyPatient } = pulmonologySlice.actions;
export default pulmonologySlice.reducer;